import { GetterTree } from 'vuex';
import { AdventuresState, AdventureDraft, AdventurePath } from './types';

type AdventuresGetterContext = GetterTree<AdventuresState, any>;

const getters: AdventuresGetterContext = {
  adventureDraft(state: AdventuresState): AdventureDraft {
    return { ...state.adventureDraft, ...state.pendingEdits };
  },

  pendingEdits(state: AdventuresState): Partial<AdventureDraft> {
    return state.pendingEdits;
  },

  adventureId(state: AdventuresState, gettersContext: any): number | null {
    return gettersContext.adventureDraft.id;
  },

  flashMessage(state: AdventuresState): string | undefined {
    return state.flashMessage;
  },

  apolloMutationInput(state: AdventuresState, gettersContext: any): any {
    const { adventureDraft } = gettersContext;
    const output = {} as any;

    if (adventureDraft.adventureCategoryId) output.adventureCategoryId = adventureDraft.adventureCategoryId;
    if (adventureDraft.ageRangeIds.length) output.ageRangeIds = adventureDraft.ageRangeIds;
    if (adventureDraft.description) output.description = adventureDraft.description;
    if (adventureDraft.sitterPassion) output.sitterPassion = adventureDraft.sitterPassion;
    if (adventureDraft.title) output.title = adventureDraft.title;
    if (adventureDraft.duration >= 60) output.duration = adventureDraft.duration;
    if (adventureDraft.adventureSitterSupplies[0]?.item) {
      output.adventureSitterSupplies = adventureDraft.adventureSitterSupplies.map(({ item }: any) => {
        return { item };
      });
    }
    if (adventureDraft.adventureParentSupplies[0]?.item) {
      output.adventureParentSupplies = adventureDraft.adventureParentSupplies.map(({ item }: any) => {
        return { item };
      });
    }
    if (adventureDraft.surcharge >= 0) output.surcharge = adventureDraft.surcharge;
    if (adventureDraft.adventureCategoryImage?.id) output.adventureCategoryImageId = adventureDraft.adventureCategoryImage.id;

    return output;
  },

  resumePath(state: AdventuresState): string {
    const { adventureDraft } = state;
    const allPaths: AdventurePath[] = [
      {
        name: 'Category',
        path: '/adventures/new/category',
        data: adventureDraft.adventureCategoryId,
      },
      {
        title: 'Age',
        path: '/adventures/new/age_range',
        data: adventureDraft.ageRangeIds,
      },
      {
        title: 'Description',
        path: '/adventures/new/description',
        data: adventureDraft.description,
      },
      {
        name: 'Promote',
        path: '/adventures/new/passion',
        data: adventureDraft.sitterPassion,
      },
      {
        name: 'Title',
        path: '/adventures/new/title',
        data: adventureDraft.title,
      },
      {
        name: 'Duration',
        path: '/adventures/new/duration',
        data: adventureDraft.duration,
      },
      {
        name: 'Supplies',
        path: '/adventures/new/supplies',
        data: [...adventureDraft.adventureParentSupplies, ...adventureDraft.adventureSitterSupplies],
      },
      {
        name: 'Rate',
        path: '/adventures/new/rate',
        data: adventureDraft.surcharge,
      },
      {
        name: 'Photo',
        path: '/adventures/new/photo',
        data: adventureDraft.adventureCategoryImage,
      },
      {
        name: 'Availability',
        path: '/adventures/new/availability',
        data: adventureDraft.sitterAvailability,
      },
    ];

    const emptyAvailability = '{"monday":[],"tuesday":[],"wednesday":[],"thursday":[],"friday":[],"saturday":[],"sunday":[]}';

    return (
      allPaths.find((path) => {
        const stringifyPathObject = JSON.stringify(path.data);
        return (
          path.data === null ||
          path.data === -1 ||
          (path.data as any)?.id === 0 ||
          (Array.isArray(path.data) && path.data.length === 0) ||
          stringifyPathObject === emptyAvailability ||
          stringifyPathObject === '{}'
        );
      })?.path || '/adventures/new/review'
    );
  },

  parentSuppliesList(state: AdventuresState, gettersContext: any): string[] {
    const { adventureDraft } = gettersContext;
    const supplies = adventureDraft.adventureParentSupplies.length ? adventureDraft.adventureParentSupplies : [{ item: '' }];
    return supplies.map(({ item }: any) => item);
  },

  sitterSuppliesList(state: AdventuresState, gettersContext: any): string[] {
    const { adventureDraft } = gettersContext;
    const supplies = adventureDraft.adventureSitterSupplies.length ? adventureDraft.adventureSitterSupplies : [{ item: '' }];
    return supplies?.map(({ item }: any) => item);
  },

  previousRoute(state: AdventuresState): string | undefined {
    return state.previousRouteStack.slice(-1)[0];
  },
};

export default getters;
